<template>
  <div
    :class="`${
      displayBorder
        ? 'tw-rounded-[6px] tw-p-[8px] md:tw-p-[20px] tw-bg-[#FFF]'
        : ''
    }`"
    :style="displayBorder ? 'border: 1px solid var(--UI-rule, #eff0f1)' : ''"
  >
    <div
      v-if="displayBorder"
      class="md:tw-flex tw-justify-between tw-leading-[20px] tw-px-[5px] tw-pb-[20px] tw-pt-[2px]"
    >
      <div class="tw-text-[15px] tw-font-semibold tw-text-[#211F58]">
        {{ getComponentUiText("app-scan-scanning-issues-number-block-1") }}
      </div>
      <div
        v-if="data.runStatusId !== statusScanProgress.SCAN_COMPLETE"
        class="tw-text-[#878787] tw-font-normal tw-text-[13px]"
      >
        {{ getComponentUiText("app-scan-scanning-note-number-block-1") }}
        <button
          class="tw-font-medium tw-text-[#387DFF]"
          style="cursor: pointer"
          @click="refreshIssue"
        >
          {{ getComponentUiText("app-scan-scanning-refresh-number-block-1") }}
        </button>
      </div>
    </div>
    <div
      v-if="isRefreshing && webServicesId === webserviceScanningId"
      class="tw-flex tw-gap-[6px]"
    >
      <div
        v-for="index in 4"
        :key="index"
        class="tw-grid tw-items-center tw-gap-y-[6px] tw-flex-1"
      >
        <div
          class="custom-global-skeleton tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[10px] tw-font-[600] tw-rounded-t-[4px]"
        ></div>
        <div
          class="custom-global-skeleton tw-h-[36px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[13px] tw-font-[600] tw-rounded-b-[4px]"
        ></div>
      </div>
    </div>
    <div v-else-if="isRefreshing && domainId === domainScanningId">
      <div
        v-for="index in 4"
        :key="index"
        class="tw-grid tw-items-center tw-gap-y-[6px] tw-flex-1"
      >
        <div
          class="custom-global-skeleton tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[10px] tw-font-[600] tw-rounded-t-[4px]"
        ></div>
        <div
          class="custom-global-skeleton tw-h-[36px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[13px] tw-font-[600] tw-rounded-b-[4px]"
        ></div>
      </div>
    </div>
    <div v-else class="tw-flex tw-gap-[6px]">
      <div class="tw-grid tw-items-center tw-gap-y-[6px] tw-flex-1">
        <div
          class="tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[10px] tw-font-[600] tw-rounded-t-[4px] background-item"
        >
          {{
            getComponentUiText(
              "app-scanning-page-issue-mini-dash-card-critical-1",
            )
          }}
        </div>
        <div
          class="tw-h-[36px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[13px] tw-font-[600] tw-rounded-b-[4px] background-critical"
        >
          {{ severity.critical }}
        </div>
      </div>
      <div class="tw-grid tw-items-center tw-gap-y-[6px] tw-flex-1">
        <div
          class="tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[10px] tw-font-[600] tw-rounded-t-[4px] background-item"
        >
          {{
            getComponentUiText("app-scanning-page-issue-mini-dash-card-high-1")
          }}
        </div>
        <div
          class="tw-h-[36px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[13px] tw-font-[600] tw-rounded-b-[4px] background-high"
        >
          {{ severity.high }}
        </div>
      </div>
      <div class="tw-grid tw-items-center tw-gap-y-[6px] tw-flex-1">
        <div
          class="tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[10px] tw-font-[600] tw-rounded-t-[4px] background-item"
        >
          {{
            getComponentUiText("app-scanning-page-issue-mini-dash-card-med-1")
          }}
        </div>
        <div
          class="tw-h-[36px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[13px] tw-font-[600] tw-rounded-b-[4px] background-med"
        >
          {{ severity.medium }}
        </div>
      </div>
      <div class="tw-grid tw-items-center tw-gap-y-[6px] tw-flex-1">
        <div
          class="tw-h-[30px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[10px] tw-font-[600] tw-rounded-t-[4px] background-item"
        >
          {{
            getComponentUiText("app-scanning-page-issue-mini-dash-card-low-1")
          }}
        </div>
        <div
          class="tw-h-[36px] tw-flex tw-items-center tw-justify-center tw-px-[20px] tw-py-[10px] tw-text-[13px] tw-font-[600] tw-rounded-b-[4px] background-low"
        >
          {{ severity.low }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import store from "@/store";
import { getComponentUiText } from "@/utils/utilities";
import { PropType, computed } from "vue";
import { statusScanProgress } from "@/utils/constants";

const props = defineProps({
  severity: {
    type: Object as PropType<
      Record<"critical" | "high" | "medium" | "low", number>
    >,
    required: false,
    default: () => ({
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
    }),
  },
  webServicesId: {
    type: String,
    required: true,
  },
  domainId: {
    type: String,
    required: true,
  },
  displayBorder: {
    type: Boolean,
    required: false,
    default: true,
  },
  data: {
    type: Object,
    required: true,
  },
});

const isRefreshing = computed(() => store.state.Scanning.isLoading);
const webserviceScanningId = computed(
  () => store.state.Scanning.webserviceScanningId,
);
const domainScanningId = computed<string>(
  () => store.state.Scanning.domainScanningId,
);

const refreshIssue = async () => {
  store.commit("Scanning/SET_DATA_REFRESH_LOADING", {
    isLoading: true,
    webserviceIds: props.webServicesId,
    domainIds: props.domainId,
  });
  try {
    if (props.webServicesId) {
      await store.dispatch("Scanning/refreshIssuesWebServiceScanning", {
        webserviceIds: props.webServicesId,
      });
    } else {
      await store.dispatch("Scanning/refreshIssuesDomainScanning", {
        domainIds: props.domainId,
      });
    }
  } catch (error) {
    console.log(error);
  } finally {
    store.commit("Scanning/SET_DATA_REFRESH_LOADING", {
      isLoading: false,
      webserviceIds: null,
      domainIds: null,
    });
  }
};
</script>

<style lang="scss" scoped>
.background-item {
  background: var(--UI-rule-50, rgba(239, 240, 241, 0.5));
  color: #211f58;
}
.background-critical {
  color: #f7553c;
  background: var(--UI-red-orange-soft, #fee5e2);
}
.background-high {
  color: #fe9a05;
  background: var(
    --placeholder-orange-soft,
    linear-gradient(
      0deg,
      rgba(254, 154, 5, 0.15) 0%,
      rgba(254, 154, 5, 0.15) 100%
    ),
    #fff
  );
}
.background-med {
  color: #904bd6;
  background: rgba(144, 75, 214, 0.1);
}
.background-low {
  color: #01893d;
  background: var(--UI-green-soft, #d9ede2);
}
</style>
